/**
 * Supabase Client Configuration
 * Prozpector CRM
 */

import { createClient } from '@supabase/supabase-js';

// Get Supabase configuration from environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder-key';

// Create Supabase client (will use mock data if env vars not set)
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

// Database types for TypeScript
export type Database = {
  public: {
    Tables: {
      // Phase 3: Multi-tenancy tables
      organizations: {
        Row: {
          id: string;
          name: string;
          slug: string;
          owner_id: string | null;
          plan: 'free' | 'starter' | 'pro' | 'agency';
          plan_status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'paused';
          stripe_customer_id: string | null;
          stripe_subscription_id: string | null;
          trial_ends_at: string | null;
          current_period_starts_at: string | null;
          current_period_ends_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['organizations']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['organizations']['Insert']>;
      };
      organization_members: {
        Row: {
          id: string;
          organization_id: string;
          user_id: string;
          role: 'owner' | 'admin' | 'member';
          invited_by: string | null;
          invited_at: string;
          accepted_at: string | null;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['organization_members']['Row'], 'id' | 'invited_at' | 'created_at'>;
        Update: Partial<Database['public']['Tables']['organization_members']['Insert']>;
      };
      usage_tracking: {
        Row: {
          id: string;
          organization_id: string;
          period_start: string;
          period_end: string;
          prospects_count: number;
          emails_sent: number;
          linkedin_actions: number;
          sequences_active: number;
          templates_count: number;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['usage_tracking']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['usage_tracking']['Insert']>;
      };
      billing_events: {
        Row: {
          id: string;
          organization_id: string;
          stripe_event_id: string;
          event_type: string;
          amount: number | null;
          currency: string;
          invoice_url: string | null;
          receipt_url: string | null;
          metadata: Record<string, any> | null;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['billing_events']['Row'], 'id' | 'created_at'>;
        Update: Partial<Database['public']['Tables']['billing_events']['Insert']>;
      };
      onboarding_progress: {
        Row: {
          id: string;
          user_id: string;
          organization_id: string;
          steps_completed: {
            account_created: boolean;
            email_connected: boolean;
            linkedin_connected: boolean;
            prospects_imported: boolean;
            sequence_created: boolean;
            first_outreach_sent: boolean;
          };
          completed_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['onboarding_progress']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['onboarding_progress']['Insert']>;
      };
      prospects: {
        Row: {
          id: string;
          user_id: string;
          organization_id: string | null;
          company_name: string;
          company_industry: string | null;
          company_size: string | null;
          company_location: string | null;
          company_website: string | null;
          contact_first_name: string | null;
          contact_last_name: string | null;
          contact_email: string | null;
          contact_phone: string | null;
          contact_title: string | null;
          contact_linkedin_url: string | null;
          stage: 'New' | 'Contacted' | 'Clicked/Replied' | 'Call Booked' | 'Proposal' | 'Won' | 'Lost';
          lead_source: string | null;
          tags: string[] | null;
          notes: string | null;
          // Phase 2: Automation fields
          linkedin_url: string | null;
          email_verified: boolean;
          unsubscribed: boolean;
          unsubscribed_at: string | null;
          last_outreach_date: string | null;
          total_outreach_count: number;
          replied_on_channel: 'linkedin' | 'email' | 'phone' | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['prospects']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['prospects']['Insert']>;
      };
      lead_sources: {
        Row: {
          id: string;
          user_id: string;
          organization_id: string | null;
          name: string;
          type: 'linkedin_scrape' | 'apollo_search' | 'manual_import' | 'csv_import';
          search_criteria: Record<string, any>;
          last_run_at: string | null;
          total_leads_imported: number;
          status: 'active' | 'paused' | 'completed';
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['lead_sources']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['lead_sources']['Insert']>;
      };
      outreach_sequences: {
        Row: {
          id: string;
          user_id: string;
          organization_id: string | null;
          name: string;
          description: string | null;
          steps: Array<{
            day: number;
            channel: 'linkedin_request' | 'linkedin_message' | 'email';
            template_id: string;
            template_name?: string;
          }>;
          active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['outreach_sequences']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['outreach_sequences']['Insert']>;
      };
      prospect_sequence_enrollments: {
        Row: {
          id: string;
          prospect_id: string;
          sequence_id: string;
          organization_id: string | null;
          current_step_index: number;
          enrollment_date: string;
          next_action_date: string | null;
          status: 'active' | 'paused' | 'completed' | 'responded';
          paused_reason: 'replied' | 'manual_pause' | 'unsubscribed' | 'bounced' | null;
          completed_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['prospect_sequence_enrollments']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['prospect_sequence_enrollments']['Insert']>;
      };
      outreach_actions: {
        Row: {
          id: string;
          prospect_id: string;
          sequence_enrollment_id: string | null;
          organization_id: string | null;
          action_type: 'linkedin_request' | 'linkedin_message' | 'email_sent' | 'phone_call' | 'manual_action';
          channel: 'linkedin' | 'email' | 'phone' | 'other';
          template_used: string | null;
          status: 'queued' | 'sent' | 'failed' | 'replied' | 'opened' | 'clicked' | 'bounced';
          sent_at: string | null;
          replied_at: string | null;
          opened_at: string | null;
          clicked_at: string | null;
          error_message: string | null;
          metadata: Record<string, any>;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['outreach_actions']['Row'], 'id' | 'created_at'>;
        Update: Partial<Database['public']['Tables']['outreach_actions']['Insert']>;
      };
      daily_limits: {
        Row: {
          id: string;
          user_id: string;
          organization_id: string | null;
          date: string;
          linkedin_requests_sent: number;
          linkedin_messages_sent: number;
          emails_sent: number;
          linkedin_request_limit: number;
          linkedin_message_limit: number;
          email_limit: number;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['daily_limits']['Row'], 'id' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['daily_limits']['Insert']>;
      };
      activities: {
        Row: {
          id: string;
          user_id: string;
          organization_id: string | null;
          prospect_id: string;
          activity_type: 'email_sent' | 'linkedin_connection' | 'linkedin_message' | 'call_completed' | 'meeting_scheduled' | 'note_added' | 'proposal_sent' | 'other';
          description: string;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['activities']['Row'], 'id' | 'created_at'>;
        Update: Partial<Database['public']['Tables']['activities']['Insert']>;
      };
      email_templates: {
        Row: {
          id: string;
          user_id: string;
          organization_id: string | null;
          name: string;
          subject: string;
          body: string;
          variables: string[] | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['email_templates']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['email_templates']['Insert']>;
      };
      tasks: {
        Row: {
          id: string;
          user_id: string;
          organization_id: string | null;
          prospect_id: string | null;
          description: string;
          due_date: string | null;
          completed: boolean;
          completed_at: string | null;
          priority: 'low' | 'medium' | 'high';
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['tasks']['Row'], 'id' | 'created_at' | 'updated_at' | 'completed_at'>;
        Update: Partial<Database['public']['Tables']['tasks']['Insert']>;
      };
    };
  };
};
